import { useEffect, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Briefcase, Plus } from "lucide-react";
import AddressAutocomplete from "../components/AddressAutocomplete";
import PropertyImage from "../components/PropertyImage";
import { formatUSD, parseUSD } from "../lib/currency";

import { PROPERTY_TYPES, defaultForm } from "../components/deals/constants";
import { calcMonthlyPayment } from "../components/deals/dealMath";
import { Field, SectionHeader, ComputedField } from "../components/deals/atoms";
import PipelineSummary from "../components/deals/PipelineSummary";
import EmptyState from "../components/deals/EmptyState";
import DealCard from "../components/deals/DealCard";
import DealPhotosSection from "../components/deals/PhotosSection";
import DealDetailDrawer from "../components/deals/DealDetailDrawer";

export default function Deals() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null); // { deal, invitations, comments }
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/deals");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load deals");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(defaultForm()); setOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      ...defaultForm(),
      ...d,
      sqft: d.sqft ?? "",
      units: d.units ?? "",
      year_built: d.year_built ?? "",
      purchase_price: d.purchase_price ?? "",
      noi: d.noi ?? "",
      cap_rate: d.cap_rate ?? "",
      projected_irr: d.projected_irr ?? "",
      equity_required: d.equity_required ?? "",
      hold_period_years: d.hold_period_years ?? "",
      minimum_investment: d.minimum_investment ?? "",
      loan_amount: d.loan_amount ?? "",
      ltv: d.ltv ?? "",
      interest_rate: d.interest_rate ?? "",
      amortization_years: d.amortization_years ?? "",
      loan_type: d.loan_type || "amortizing",
      transaction_type: d.transaction_type || "purchase",
      purchase_date: d.purchase_date || "",
      existing_liens: d.existing_liens ?? "",
      as_is_value: d.as_is_value ?? "",
      arv_value: d.arv_value ?? "",
      tax_value: d.tax_value ?? "",
      improvements: d.improvements ?? "",
      address_verified: d.lat != null && d.lng != null,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.name?.trim() || !form.address?.trim()) {
      toast.error("Name and Address are required");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      delete payload.address_verified;
      // Cast numeric strings
      for (const k of ["sqft","units","year_built","purchase_price","noi","cap_rate","projected_irr",
                       "equity_required","hold_period_years","minimum_investment",
                       "loan_amount","ltv","interest_rate","amortization_years",
                       "existing_liens","as_is_value","arv_value","tax_value","improvements"]) {
        if (payload[k] === "" || payload[k] == null) payload[k] = null;
        else payload[k] = Number(payload[k]);
      }
      if (editing) {
        await api.put(`/deals/${editing.id}`, payload);
        toast.success("Deal updated");
      } else {
        await api.post("/deals", payload);
        toast.success("Deal created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete deal "${d.name}"? Invitations and comments will be removed.`)) return;
    try {
      await api.delete(`/deals/${d.id}`);
      toast.success("Deal deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const openDetail = async (d) => {
    try {
      const { data } = await api.get(`/deals/${d.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load deal");
    }
  };

  return (
    <DashboardShell>
      <div className="space-y-6" data-testid="deals-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-500">
              <Briefcase className="w-3.5 h-3.5" /> Capital Raise
            </div>
            <h1 className="text-3xl font-semibold text-slate-900 mt-1">Deals Submission</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Package an investment opportunity and send it to your investor list. Track who viewed, soft-circled, or committed — and field Q&amp;A in one thread per deal.
            </p>
          </div>
          <button
            onClick={openCreate}
            data-testid="deals-create-btn"
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded-sm text-sm font-mono uppercase tracking-widest"
          >
            <Plus className="w-4 h-4" /> New Deal
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-slate-400 font-mono">Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState onCreate={openCreate} />
        ) : (
          <>
            <PipelineSummary items={items} />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="deals-grid">
              {items.map((d) => <DealCard key={d.id} deal={d} onEdit={openEdit} onDelete={remove} onOpen={openDetail} />)}
            </div>
          </>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Deal" : "New Deal"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Deal Name *">
              <input required className="input-clean w-full" data-testid="deal-name-input"
                value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}
                placeholder="e.g. 525 Market — Class-A Office" />
            </Field>

            <Field label="Property Address *">
              <AddressAutocomplete
                value={form.address}
                verified={form.address_verified}
                dataTestId="deal-address-input"
                required
                onChange={(v) => setForm((f) => ({ ...f, address: v }))}
                onVerified={({ formatted_address, lat, lng, place_id }) => {
                  setForm((f) => ({
                    ...f,
                    address: formatted_address,
                    formatted_address, lat, lng, place_id,
                    address_verified: true,
                  }));
                }}
                onReset={() => setForm((f) => ({
                  ...f, address_verified: false, lat: null, lng: null, formatted_address: null, place_id: null,
                }))}
                placeholder="Start typing the address…"
              />
            </Field>

            <div data-testid="deal-image-preview">
              <PropertyImage
                address={form.address}
                pid={null}
                size="600x240"
                className="w-full"
                refreshKey={form.address}
              />
              <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-slate-400">
                Live preview · powered by Google Street View, falls back to aerial.
              </div>
            </div>

            <SectionHeader>Property</SectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Field label="Property Type" className="md:col-span-2">
                <select className="input-clean w-full" value={form.property_type}
                  onChange={(e)=>setForm({...form, property_type:e.target.value})} data-testid="deal-type-select">
                  {PROPERTY_TYPES.map(t =>
                    <option key={t.value} value={t.value}>{t.label}</option>
                  )}
                </select>
              </Field>
              <Field label="Year Built">
                <input type="number" className="input-clean w-full font-mono" value={form.year_built}
                  onChange={(e)=>setForm({...form, year_built:e.target.value})}
                  data-testid="deal-year-built-input" />
              </Field>
              <Field label="Sqft">
                <input type="number" className="input-clean w-full font-mono" value={form.sqft}
                  onChange={(e)=>setForm({...form, sqft:e.target.value})}
                  data-testid="deal-sqft-input" />
              </Field>
              <Field label="Units">
                <input type="number" className="input-clean w-full font-mono" value={form.units}
                  onChange={(e)=>setForm({...form, units:e.target.value})}
                  data-testid="deal-units-input" />
              </Field>
            </div>

            {/* Hero photo slots + Additional Photos gallery (extracted to a
                dedicated subcomponent in components/deals/PhotosSection.jsx) */}
            <DealPhotosSection editing={editing} />

            <SectionHeader>Transaction</SectionHeader>
            {/* Row 1: Deal logistics — what kind of deal, when, hold timeline */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Transaction Type">
                <select className="input-clean w-full" value={form.transaction_type || "purchase"}
                  onChange={(e)=>setForm({...form, transaction_type:e.target.value})}
                  data-testid="deal-transaction-type-select">
                  <option value="purchase">Purchase</option>
                  <option value="refinance">Refinance</option>
                </select>
              </Field>
              <Field label={form.transaction_type === "refinance" ? "Refi Date" : "Purchase Date"}>
                <input type="date" className="input-clean w-full" value={form.purchase_date || ""}
                  onChange={(e)=>setForm({...form, purchase_date:e.target.value})}
                  data-testid="deal-purchase-date-input" />
              </Field>
              <Field label="Target Close Date">
                <input type="date" className="input-clean w-full" value={form.target_close_date || ""}
                  onChange={(e)=>setForm({...form, target_close_date:e.target.value})} />
              </Field>
              <Field label="Hold (years)">
                <input type="number" step="0.5" className="input-clean w-full font-mono" value={form.hold_period_years}
                  onChange={(e)=>setForm({...form, hold_period_years:e.target.value})} />
              </Field>
            </div>

            {/* Row 2: Pricing & current capital stack — what the asset costs and is worth today */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label={form.transaction_type === "refinance" ? "Current Value" : "Purchase Price"}>
                <input className="input-clean w-full font-mono" inputMode="decimal"
                  value={formatUSD(form.purchase_price)}
                  onChange={(e)=>setForm({...form, purchase_price:parseUSD(e.target.value)})}
                  placeholder="$0" data-testid="deal-price-input" />
              </Field>
              <Field label="Existing Liens">
                <input className="input-clean w-full font-mono" inputMode="decimal"
                  value={formatUSD(form.existing_liens)}
                  onChange={(e)=>setForm({...form, existing_liens:parseUSD(e.target.value)})}
                  placeholder="$0" data-testid="deal-existing-liens-input" />
              </Field>
              <Field label="As-Is Value">
                <input className="input-clean w-full font-mono" inputMode="decimal"
                  value={formatUSD(form.as_is_value)}
                  onChange={(e)=>setForm({...form, as_is_value:parseUSD(e.target.value)})}
                  placeholder="$0" data-testid="deal-as-is-value-input" />
              </Field>
              <Field label="Tax-Assessed Value">
                <input className="input-clean w-full font-mono" inputMode="decimal"
                  value={formatUSD(form.tax_value)}
                  onChange={(e)=>setForm({...form, tax_value:parseUSD(e.target.value)})}
                  placeholder="$0" data-testid="deal-tax-value-input" />
              </Field>
            </div>

            {/* Row 3: Value-add math — capex + expected exit. ARV reads
                clearly when sitting right after Improvements (the lender's
                "Stabilized Value" pencil). */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Improvements (Capex)">
                <input className="input-clean w-full font-mono" inputMode="decimal"
                  value={formatUSD(form.improvements)}
                  onChange={(e)=>setForm({...form, improvements:parseUSD(e.target.value)})}
                  placeholder="$0" data-testid="deal-improvements-input" />
              </Field>
              <Field label="Expected ARV">
                <input className="input-clean w-full font-mono" inputMode="decimal"
                  value={formatUSD(form.arv_value)}
                  onChange={(e)=>setForm({...form, arv_value:parseUSD(e.target.value)})}
                  placeholder="$0" data-testid="deal-arv-value-input" />
              </Field>
              <Field label="All-In Basis — auto" className="md:col-span-2">
                <ComputedField
                  value={(() => {
                    const p = Number(form.purchase_price) || 0;
                    const i = Number(form.improvements) || 0;
                    return p + i > 0
                      ? `$${(p + i).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                      : "—";
                  })()}
                  helper="Purchase Price + Improvements"
                  testid="deal-all-in-basis-computed"
                />
              </Field>
            </div>

            <SectionHeader>Returns &amp; Equity</SectionHeader>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="NOI (Year 1)">
                <input className="input-clean w-full font-mono" inputMode="decimal"
                  value={formatUSD(form.noi)}
                  onChange={(e)=>setForm({...form, noi:parseUSD(e.target.value)})}
                  placeholder="$0" data-testid="deal-noi-input" />
              </Field>
              <Field label="Cap Rate (%) — auto">
                <ComputedField
                  value={
                    Number(form.noi) && Number(form.purchase_price)
                      ? ((Number(form.noi) / Number(form.purchase_price)) * 100).toFixed(2) + "%"
                      : "—"
                  }
                  helper="NOI ÷ Purchase Price"
                  testid="deal-cap-rate-computed"
                />
              </Field>
              <Field label="DSCR — auto">
                <ComputedField
                  value={(() => {
                    const pmt = calcMonthlyPayment({
                      principal: form.loan_amount,
                      annualRatePct: form.interest_rate,
                      termYears: form.amortization_years,
                      loanType: form.loan_type,
                    });
                    const annualDebtService = pmt * 12;
                    if (!Number(form.noi) || annualDebtService <= 0) return "—";
                    return (Number(form.noi) / annualDebtService).toFixed(2) + "x";
                  })()}
                  helper="NOI ÷ Annual Debt Service"
                  testid="deal-dscr-computed"
                />
              </Field>
              <Field label="Projected IRR (%)">
                <input type="number" step="0.01" className="input-clean w-full font-mono" value={form.projected_irr}
                  onChange={(e)=>setForm({...form, projected_irr:e.target.value})}
                  data-testid="deal-irr-input" />
              </Field>
              <Field label="Equity Required">
                <input className="input-clean w-full font-mono" inputMode="decimal"
                  value={formatUSD(form.equity_required)}
                  onChange={(e)=>setForm({...form, equity_required:parseUSD(e.target.value)})}
                  placeholder="$0" />
              </Field>
              <Field label="Min. Investment">
                <input className="input-clean w-full font-mono" inputMode="decimal"
                  value={formatUSD(form.minimum_investment)}
                  onChange={(e)=>setForm({...form, minimum_investment:parseUSD(e.target.value)})}
                  placeholder="$0" />
              </Field>
            </div>

            <SectionHeader>Loan / Financing</SectionHeader>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Loan Amount">
                <input className="input-clean w-full font-mono" inputMode="decimal"
                  value={formatUSD(form.loan_amount)}
                  onChange={(e)=>setForm({...form, loan_amount:parseUSD(e.target.value)})}
                  placeholder="$0" data-testid="deal-loan-amount-input" />
              </Field>
              <Field label="LTV (%) — auto">
                <ComputedField
                  value={
                    Number(form.loan_amount) && Number(form.purchase_price)
                      ? ((Number(form.loan_amount) / Number(form.purchase_price)) * 100).toFixed(2) + "%"
                      : "—"
                  }
                  helper="Loan ÷ Purchase Price"
                  testid="deal-ltv-computed"
                />
              </Field>
              <Field label="Rate (%)">
                <input type="number" step="0.01" className="input-clean w-full font-mono" value={form.interest_rate}
                  onChange={(e)=>setForm({...form, interest_rate:e.target.value})}
                  data-testid="deal-rate-input" />
              </Field>
              <Field label="Loan Type">
                <select className="input-clean w-full" value={form.loan_type || "amortizing"}
                  onChange={(e)=>setForm({...form, loan_type:e.target.value})}
                  data-testid="deal-loan-type-select">
                  <option value="amortizing">Amortizing</option>
                  <option value="interest_only">Interest Only</option>
                </select>
              </Field>
              <Field label={form.loan_type === "interest_only" ? "Term (years)" : "Amort. (years)"}>
                <input type="number" step="0.5" className="input-clean w-full font-mono" value={form.amortization_years}
                  onChange={(e)=>setForm({...form, amortization_years:e.target.value})}
                  data-testid="deal-amort-years-input" />
              </Field>
              <Field label="Monthly Payment — auto" className="md:col-span-3">
                <ComputedField
                  value={(() => {
                    const pmt = calcMonthlyPayment({
                      principal: form.loan_amount,
                      annualRatePct: form.interest_rate,
                      termYears: form.amortization_years,
                      loanType: form.loan_type,
                    });
                    return pmt > 0
                      ? `$${pmt.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}/mo`
                      : "—";
                  })()}
                  helper={
                    form.loan_type === "interest_only"
                      ? "Loan × (Rate ÷ 12) — interest only, balloon at term"
                      : "Standard amortization · P&I"
                  }
                  testid="deal-monthly-payment-computed"
                />
              </Field>
            </div>
            <Field label="Loan Notes">
              <textarea rows={2} className="input-clean w-full" value={form.loan_notes || ""}
                onChange={(e)=>setForm({...form, loan_notes:e.target.value})}
                placeholder="Lender, terms, recourse, prepayment, etc." />
            </Field>

            <SectionHeader>Sponsor &amp; Summary</SectionHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Sponsor Name">
                <input className="input-clean w-full" value={form.sponsor_name || ""}
                  onChange={(e)=>setForm({...form, sponsor_name:e.target.value})}
                  placeholder="Your company / GP entity" />
              </Field>
              <Field label="Track Record (1-2 lines)">
                <input className="input-clean w-full" value={form.sponsor_track_record || ""}
                  onChange={(e)=>setForm({...form, sponsor_track_record:e.target.value})}
                  placeholder="$120M AUM · 14 closed deals · 22% avg IRR" />
              </Field>
            </div>
            <Field label="Executive Summary">
              <textarea rows={5} className="input-clean w-full" value={form.executive_summary || ""}
                onChange={(e)=>setForm({...form, executive_summary:e.target.value})}
                placeholder="Investment thesis, business plan, key risks…"
                data-testid="deal-summary-input" />
            </Field>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" onClick={()=>setOpen(false)}
                className="px-4 py-2 rounded-sm text-sm font-mono uppercase tracking-widest text-slate-500 hover:text-slate-800">
                Cancel
              </button>
              <button type="submit" disabled={busy} data-testid="deal-save-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white px-5 py-2 rounded-sm text-sm font-mono uppercase tracking-widest disabled:opacity-50">
                {busy ? "Saving…" : (editing ? "Save Changes" : "Create Deal")}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail / Manage panel */}
      {detail && (
        <DealDetailDrawer
          detail={detail}
          onClose={()=>setDetail(null)}
          onChanged={async ()=>{ await load(); const { data } = await api.get(`/deals/${detail.deal.id}`); setDetail(data); }}
        />
      )}
    </DashboardShell>
  );
}
